import { useState, useEffect, useCallback, useRef } from 'react'

interface UseAppUpdateState {
  /** True when a newer service worker has installed and is ready to take over. */
  updateAvailable: boolean
  /** Reload the page so the new service worker becomes the controller. */
  applyUpdate: () => void
  /** Hide the banner for the current update without reloading. */
  dismiss: () => void
  /** Manually trigger an update check (bypasses the 5-min poll). Resolves to
   *  the outcome so a UI can show "up to date" / "update available" / "error". */
  checkForUpdate: () => Promise<UpdateCheckResult>
}

export type UpdateCheckResult =
  | 'up-to-date'
  | 'update-available'
  | 'unsupported'
  | 'error'

const POLL_INTERVAL_MS = 5 * 60 * 1000 // 5 min

/**
 * Detects when a newer service worker has been installed (i.e., a new build
 * has been deployed) and exposes a refresh handle.
 *
 * Mechanism:
 *   1. registration.update() is called every 5 min and on visibilitychange.
 *      It re-fetches /sw.js and starts an install pipeline if bytes differ.
 *   2. When the new SW reaches the `installed` state AND there is already a
 *      controller (i.e., this isn't a first-time install), we set
 *      updateAvailable = true.
 *   3. applyUpdate() reloads. The current sw.js calls self.skipWaiting() in
 *      install, so by the time we reach this point the new SW has already
 *      activated and the reload picks up fresh JS served by it.
 *   4. dismiss() hides the banner only for this update; a future updatefound
 *      resets it.
 */
export function useAppUpdate(): UseAppUpdateState {
  const [updateAvailable, setUpdateAvailable] = useState(false)
  const [dismissed, setDismissed] = useState(false)
  const dismissedRef = useRef(false)

  useEffect(() => {
    dismissedRef.current = dismissed
  }, [dismissed])

  useEffect(() => {
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return
    let cancelled = false
    let pollTimer: ReturnType<typeof setInterval> | null = null
    let cleanupRegistration: (() => void) | null = null

    const surface = () => {
      if (cancelled) return
      // Reset dismissal so a NEW update reappears even if the user dismissed
      // a previous one this session.
      dismissedRef.current = false
      setDismissed(false)
      setUpdateAvailable(true)
    }

    const watchInstalling = (sw: ServiceWorker) => {
      const onStateChange = () => {
        if (sw.state === 'installed' && navigator.serviceWorker.controller) {
          surface()
        }
      }
      sw.addEventListener('statechange', onStateChange)
    }

    navigator.serviceWorker.ready
      .then((reg) => {
        if (cancelled) return

        // If a worker is already waiting (we missed `updatefound` because the
        // tab was backgrounded when it fired), surface immediately.
        if (reg.waiting && navigator.serviceWorker.controller) {
          surface()
        }

        // Observe future updates.
        const onUpdateFound = () => {
          if (reg.installing) watchInstalling(reg.installing)
        }
        reg.addEventListener('updatefound', onUpdateFound)

        // Periodic check + on tab regaining focus.
        const checkForUpdate = () => {
          reg.update().catch(() => {
            /* offline / network error — try again next interval */
          })
        }
        pollTimer = setInterval(checkForUpdate, POLL_INTERVAL_MS)

        const onVisibilityChange = () => {
          if (document.visibilityState === 'visible') checkForUpdate()
        }
        document.addEventListener('visibilitychange', onVisibilityChange)

        cleanupRegistration = () => {
          reg.removeEventListener('updatefound', onUpdateFound)
          document.removeEventListener('visibilitychange', onVisibilityChange)
        }
      })
      .catch(() => {
        // Service worker never became ready. Nothing to do — stay hidden.
      })

    return () => {
      cancelled = true
      if (pollTimer != null) clearInterval(pollTimer)
      cleanupRegistration?.()
    }
  }, [])

  const applyUpdate = useCallback(() => {
    // The current sw.js does self.skipWaiting() in its install handler, so the
    // newly-installed SW activates immediately without waiting for tabs to
    // close. By the time the user taps Refresh, the new SW is the controller
    // and a reload will pick up fresh JS.
    //
    // If a future sw.js drops skipWaiting, we'd need to postMessage SKIP_WAITING
    // and listen for controllerchange before reloading. Belt-and-suspenders for
    // that future: if a waiting worker exists, ping it before reload.
    if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistration().then((reg) => {
        reg?.waiting?.postMessage({ type: 'SKIP_WAITING' })
      }).catch(() => {})
    }
    window.location.reload()
  }, [])

  const dismiss = useCallback(() => {
    setDismissed(true)
    setUpdateAvailable(false)
  }, [])

  // Manually fire reg.update() and wait briefly to see whether a new worker
  // begins installing. Resolves to the outcome so callers can render
  // "up to date" vs "update available" without subscribing to state.
  // Used by Settings → About → "Check for updates" so users on a stale PWA
  // cache have a self-service path that doesn't depend on the 5-min poll.
  const checkForUpdate = useCallback(async (): Promise<UpdateCheckResult> => {
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
      return 'unsupported'
    }
    try {
      const reg = await navigator.serviceWorker.getRegistration()
      if (!reg) return 'unsupported'

      // If a worker is already waiting we know there's an update — surface
      // it immediately without doing another network round-trip.
      if (reg.waiting && navigator.serviceWorker.controller) {
        dismissedRef.current = false
        setDismissed(false)
        setUpdateAvailable(true)
        return 'update-available'
      }

      // Force a fresh sw.js fetch. If bytes differ from the registered one,
      // the install pipeline begins and reg.installing becomes non-null.
      await reg.update()

      if (reg.installing) {
        // Wait for the new SW to finish installing so we can give a definitive
        // answer instead of "checking…". Cap the wait so a flaky network
        // doesn't leave the button spinning forever.
        const installing = reg.installing
        return await new Promise<UpdateCheckResult>((resolve) => {
          const timeout = setTimeout(() => {
            installing.removeEventListener('statechange', onState)
            resolve('update-available')
          }, 15_000)
          const onState = () => {
            if (installing.state === 'installed') {
              clearTimeout(timeout)
              installing.removeEventListener('statechange', onState)
              if (navigator.serviceWorker.controller) {
                dismissedRef.current = false
                setDismissed(false)
                setUpdateAvailable(true)
                resolve('update-available')
              } else {
                // First-time SW install (no controller yet) — not an update,
                // just bootstrapping. The user is already on the latest bytes.
                resolve('up-to-date')
              }
            } else if (installing.state === 'redundant') {
              clearTimeout(timeout)
              installing.removeEventListener('statechange', onState)
              resolve('error')
            }
          }
          installing.addEventListener('statechange', onState)
        })
      }

      return 'up-to-date'
    } catch {
      return 'error'
    }
  }, [])

  // Banner only renders when there's an update AND the user hasn't dismissed
  // this particular update yet.
  return {
    updateAvailable: updateAvailable && !dismissed,
    applyUpdate,
    dismiss,
    checkForUpdate,
  }
}
